import React from "react";
import { useSelector } from "react-redux";
import { Table, Empty } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { useTrialBalanceData } from "./useTrialBalanceData";
import {
  currentDateStartTime,
  toCompanyDateOnlyforReportDateFilter,
} from "../../../../lib/functions";
import "../../../../components/common/financial-print.css"

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TrialBalanceTable = ({ printMode = false }) => {
  const {
    balances = {},
    loading = false,
    error = null,
    filters = {},
  } = useSelector((state) => state.trialBalance || {});
  const authInfo = useSelector(selectAuthInfo);
  const institutionData = useSelector((state) => state.lookups?.institutionData);
  const isIndia =
    authInfo?.country?.toLowerCase()?.trim() == "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const navigate = useNavigate();

  const routeMap = {
    "Cash In Hand": "/reports/account-balance",
    "Bank Accounts Balance": "/reports/account-balance",
    "Customer Due Balance": "/reports/debitors-balance",
    "Receivable From Other Branches": "/reports/branch-tran-receive-list",
    "Advance Payment to Creditors": "/reports/advance-creditor-balance",
    "Supplier Due Balance": "/reports/creditors-balance",
    "Purchase Account": "/reports/purchase-record",
    "Service Expense Account": null,
    "Purchase Return Account": "/reports/purchase-return-record",
    "Sales Account": "/reports/sales-record",
    "Service Account": null,
    "Sales Return Account": "/reports/sales-return-record",
    "Capital Account": "/reports/capitals-balance",
    "Reserve Surplus Account": "/reports/reserve-surplus-balance",
    "Direct Expense Account": "/reports/direct-expense-balance",
    "Indirect Expense Account": "/reports/indirect-expenses-balance",
    "Suspense Account": "/reports/suspense-balance",
    "Indirect Income Account": "/reports/indirect-income-balance",
    "Direct Income Account": "/reports/direct-income-balance",
    "Loans Account": "/reports/loan-balance",
    "Advance Received From Customer": "/reports/advance-debtor-balance",
    "Fixed Assets Account": "/reports/fixed-asset-balance",
    "Difference In Opening": null,
    "Vat Balance": null,
    "Investments": "/reports/investment-balance",
    "Current Assets": "/reports/current-assets-report",
    "Deposit Account": "/reports/deposit-account-report",
    "Current Liability Balance": "/reports/current-liablity-report",
  };
  const { dataSource, currency } = useTrialBalanceData();

  if (!loading && error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={error}
        style={{ padding: "48px 0" }}
      />
    );
  }

  const totals = {
    debit:  dataSource.reduce((acc, r) => acc + (r.debit  || 0), 0),
    credit: dataSource.reduce((acc, r) => acc + (r.credit || 0), 0),
  };

  // const dataSource = [
  //   {
  //     key: "cashBalance",
  //     label: "Cash In Hand",
  //     debit: balances.cashBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "bankBalance",
  //     label: "Bank Accounts Balance",
  //     debit: balances.bankBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "debtorBalance",
  //     label: "Customer Due Balance",
  //     debit: balances.debtorBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "inventoryBalance",
  //     label: "Opening Stock Value",
  //     debit: balances.opening_stock_rate_total,
  //     credit: 0,
  //   },
  //   {
  //     key: "receiveableBranchAmount",
  //     label: "Receivable From Other Branches",
  //     debit: balances.receiveableBranchAmount,
  //     credit: 0,
  //   },
  //   {
  //     key: "advanceCreditorBalance",
  //     label: "Advance Payment to Creditors",
  //     debit: balances.advanceCreditorBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "creditorBalance",
  //     label: "Supplier Due Balance",
  //     debit: 0,
  //     credit: balances.creditorBalance,
  //   },
  //   {
  //     key: "purchaseBalance",
  //     label: "Purchase Account",
  //     debit: balances.purchaseBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "serviceExpenseBalance",
  //     label: "Service Expense Account",
  //     debit: balances.serviceExpenseBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "purchaseReturnBalance",
  //     label: "Purchase Return Account",
  //     debit: 0,
  //     credit: balances.purchaseReturnBalance,
  //   },
  //   {
  //     key: "salesBalance",
  //     label: "Sales Account",
  //     debit: 0,
  //     credit: balances.salesBalance,
  //   },
  //   {
  //     key: "salesReplaceBalance",
  //     label: "Sales Replace Account",
  //     debit: 0,
  //     credit: balances.salesReplaceBalance,
  //   },
  //   {
  //     key: "serviceBalance",
  //     label: "Service Account",
  //     debit: 0,
  //     credit: balances.serviceBalance,
  //   },
  //   {
  //     key: "salesReturnBalance",
  //     label: "Sales Return Account",
  //     debit: balances.salesReturnBalance,
  //     credit: 0,
  //   },
  //   // {
  //   //   key: "stockPurchaseBalance",
  //   //   label: "Stock Purchase Balance",
  //   //   debit: balances.salesReturnBalance,
  //   //   credit: 0,
  //   // },
  //   {
  //     key: "capitalBalance",
  //     label: "Capital Account",
  //     debit: 0,
  //     credit: balances.capitalBalance,
  //   },
  //   {
  //     key: "reserveSurplusBalance",
  //     label: "Reserve & Surplus Account",
  //     debit: 0,
  //     credit: balances.reserveSurplusBalance,
  //   },
  //   {
  //     key: "directExpenseBalance",
  //     label: "Direct Expense Account",
  //     debit: balances.directExpenseBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "indirectExpenseBalance",
  //     label: "Indirect Expense Account",
  //     debit: balances.indirectExpenseBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "suspenseBalance",
  //     label: "Suspense Account",
  //     debit: balances.suspenseBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "indirectIncomeBalance",
  //     label: "Indirect Income Account",
  //     debit: 0,
  //     credit: balances.indirectIncomeBalance,
  //   },
  //   {
  //     key: "directIncomeBalance",
  //     label: "Direct Income Account",
  //     debit: 0,
  //     credit: balances.directIncomeBalance,
  //   },
  //   {
  //     key: "loanBalance",
  //     label: "Loans Account",
  //     debit: 0,
  //     credit: balances.loanBalance,
  //   },
  //   {
  //     key: "advanceDebtorBalance",
  //     label: "Advance Received From Customer",
  //     debit: 0,
  //     credit: balances.advanceDebtorBalance,
  //   },
  //   {
  //     key: "fixedAssestsBalance",
  //     label: "Fixed Assets Account",
  //     debit: balances.fixedAssestsBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "diffOpeningBalance",
  //     label: "Difference In Opening",
  //     debit: 0,
  //     credit: balances.diffOpeningBalance,
  //   },
  //   {
  //     key: "taxBalance",
  //     label: `${taxLabel} Account`,
  //     debit: 0,
  //     credit: balances.taxBalance,
  //   },
  //   {
  //     key: "investmentBalance",
  //     label: "Investments",
  //     debit: balances.investmentBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "currentAssetsBalance",
  //     label: "Current Assets",
  //     debit: balances.currentAssetsBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "depositAccountBalance",
  //     label: "Deposit Account",
  //     debit: balances.depositAccountBalance,
  //     credit: 0,
  //   },
  //   {
  //     key: "currentLiabilityBalance",
  //     label: "Current Liability Balance",
  //     debit: 0,
  //     credit: balances.currentLiabilityBalance,
  //   },
  // ];

  // const totals = {
  //   debit: dataSource.reduce((acc, r) => acc + (r.debit || 0), 0),
  //   credit: dataSource.reduce((acc, r) => acc + (r.credit || 0), 0),
  // };

  const columns = [
    {
      title: printMode ? "PARTICULARS" :"PARTICULARS ACCOUNT HEAD",
      dataIndex: "label",
      key: "label",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      render: (val) => (val || val > 0 ? fmt(val) : "------"),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      render: (val) => (val || val > 0 ? fmt(val) : "------"),
    },
  ];

  const handleRowClick = (record) => {
    const route = routeMap[record.label];
    const key = filters.toDate;
    if (route) {
      if (key) {
        // Date Wise: open the report for migration date (company creation date
        // if none is set) -> the selected date. The target reports read the
        // range in one of two URL styles — balance-style pages take the To date
        // from the path plus ?fromDate, record/expense/income pages take
        // ?dateFrom&dateTo — so both are sent; each page ignores the other.
        let query = "";
        if (filters.filterType === "Date Wise") {
          const booksStart = institutionData?.migration_date
            ? dayjs(institutionData.migration_date)
            : toCompanyDateOnlyforReportDateFilter(institutionData?.created_date);
          const from = booksStart ? currentDateStartTime(booksStart) : null;
          if (from) {
            const f = encodeURIComponent(from);
            const t = encodeURIComponent(key);
            query = `?fromDate=${f}&dateFrom=${f}&dateTo=${t}`;
          }
        }
        navigate(`${route}/${key}${query}`);
      } else {
        navigate(`${route}`);
      }
    }
  };

//   if (printMode) {
//   return (
//     <table className="tb-print-table">
//      <colgroup>
//       <col style={{ width: "52%" }} />
//       <col style={{ width: "24%" }} />
//       <col style={{ width: "24%" }} />
//     </colgroup>
//       <tbody>
//         <tr className="tb-print-heading">
//           <td>PARTICULARS</td>
//           <td style={{ textAlign: "right" }}>DEBIT</td>
//           <td style={{ textAlign: "right" }}>CREDIT</td>
//         </tr>

//         {dataSource.map((row) => (
//           <tr key={row.key} className="tb-print-row">
//             <td>{row.label}</td>

//             <td style={{ textAlign: "right" }}>
//               {row.debit || row.debit > 0
//                 ? `${authInfo.currency ?? ""} ${fmt(row.debit)}`
//                 : "------"}
//             </td>

//             <td style={{ textAlign: "right" }}>
//               {row.credit || row.credit > 0
//                 ? `${authInfo.currency ?? ""} ${fmt(row.credit)}`
//                 : "------"}
//             </td>
//           </tr>
//         ))}

//         <tr className="tb-print-total">
//           <td>Grand Total</td>

//           <td style={{ textAlign: "right" }}>
//             {authInfo.currency ?? ""} {fmt(totals.debit)}
//           </td>

//           <td style={{ textAlign: "right" }}>
//             {authInfo.currency ?? ""} {fmt(totals.credit)}
//           </td>
//         </tr>
//       </tbody>
//     </table>
//   );
// }
  if (printMode) {
  return (
    <table className="tb-print-table">
      <colgroup>
        <col style={{ width: "52%" }} />
        <col style={{ width: "24%" }} />
        <col style={{ width: "24%" }} />
      </colgroup>

      <tbody>
        <tr className="tb-print-heading">
          <td>PARTICULARS</td>
          <td style={{ textAlign: "right" }}>DEBIT</td>
          <td style={{ textAlign: "right" }}>CREDIT</td>
        </tr>

        {dataSource.map((row) => (
          <tr key={row.key} className="tb-print-row">
            <td>{row.label}</td>

            <td style={{ textAlign: "right" }}>
              {row.debit || row.debit > 0
                ? fmt(row.debit)
                : "------"}
            </td>

            <td style={{ textAlign: "right" }}>
              {row.credit || row.credit > 0
                ? fmt(row.credit)
                : "------"}
            </td>
          </tr>
        ))}

        <tr className="tb-print-total">
          <td style={{ textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(totals.debit)}
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(totals.credit)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
  return (
    <Table
      className={printMode ? "trial-balance-print" : ""}
      rowKey="key"
      columns={columns}
      dataSource={[
        ...dataSource,
        // {
        //   key: "grandTotal",
        //   label: "Grand Total",
        //   debit: totals.debit,
        //   credit: totals.credit,
        // },
      ]}
      loading={loading}
      pagination={false}
      bordered
      scroll={printMode ? undefined : { y: 500 }}     // height of scrollable area
      sticky={printMode ? false : { offsetHeader: 0 }}
      size={printMode ? "small" : "middle"}
      onRow={(record) => ({
        onClick: () => handleRowClick(record),
        style: {
          cursor: routeMap[record.label] ? "pointer" : "default",
        },
      })}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">
            <strong>
              {authInfo.currency} {fmt(totals.debit)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <strong>
              {authInfo.currency} {fmt(totals.credit)}
            </strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default TrialBalanceTable;
